import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, CheckCircle2, Mail } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatFechaHora } from '../utils/format';

// ============================================================
// Campana del menú superior (#126): alertas y tareas pendientes del
// usuario en la empresa activa. Las calcula el servidor (GET
// /notificaciones) y recuerda cuáles ya se leyeron; un aviso sale solo
// cuando el problema se resuelve. Abajo, la frecuencia del correo de
// resumen (#127). Se refresca al cambiar de empresa o de pantalla y cada
// 5 minutos con la pestaña visible.
// ============================================================

const COLOR_NIVEL = { alta: 'bg-red-500', media: 'bg-amber-500', baja: 'bg-slate-300' };
const FRECUENCIAS = [
  { valor: 'diario', label: 'Diario' },
  { valor: 'semanal', label: 'Semanal (lunes)' },
  { valor: 'nunca', label: 'Nunca' }
];

export default function CampanaNotificaciones() {
  const { empresaActiva } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [abierta, setAbierta] = useState(false);
  const [lista, setLista] = useState([]);
  const [sinLeer, setSinLeer] = useState(0);
  const [preferencia, setPreferencia] = useState(null);
  const caja = useRef(null);
  const ultimaCarga = useRef(0);

  // `forzar` al cambiar de empresa; si no, a lo sumo una vez cada 30 s.
  const cargar = (forzar = false) => {
    if (!empresaActiva || document.hidden) return;
    if (!forzar && Date.now() - ultimaCarga.current < 30000) return;
    ultimaCarga.current = Date.now();
    api.get('/notificaciones')
      .then(({ data }) => { setLista(data.notificaciones); setSinLeer(data.sin_leer); })
      .catch(() => {});
  };

  useEffect(() => {
    setLista([]);
    setSinLeer(0);
    cargar(true);
    const alVolver = () => cargar();
    const id = setInterval(alVolver, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', alVolver);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', alVolver); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  // Al cambiar de pantalla (por ejemplo, después de cargar la tasa).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [location.pathname]);

  useEffect(() => {
    if (!abierta) return undefined;
    if (!preferencia) api.get('/notificaciones/preferencia').then(({ data }) => setPreferencia(data)).catch(() => {});
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierta(false); };
    const esc = (e) => { if (e.key === 'Escape') setAbierta(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc); };
  }, [abierta, preferencia]);

  const abrirAviso = (n) => {
    setAbierta(false);
    if (!n.leida) {
      setLista((l) => l.map((x) => (x.id === n.id ? { ...x, leida: 1 } : x)));
      setSinLeer((c) => Math.max(0, c - 1));
      api.post(`/notificaciones/${n.id}/leida`).catch(() => {});
    }
    if (n.enlace) navigate(n.enlace);
  };

  const leerTodas = () => {
    setLista((l) => l.map((x) => ({ ...x, leida: 1 })));
    setSinLeer(0);
    api.post('/notificaciones/leidas').catch(() => {});
  };

  const cambiarFrecuencia = (frecuencia) => {
    const antes = preferencia;
    setPreferencia({ ...preferencia, frecuencia, por_defecto: false });
    api.put('/notificaciones/preferencia', { frecuencia })
      .then(({ data }) => setPreferencia(data))
      .catch(() => { setPreferencia(antes); alert('No se pudo guardar la preferencia de correo'); });
  };

  return (
    <div className="relative" ref={caja}>
      <button
        type="button"
        onClick={() => setAbierta((a) => !a)}
        title="Notificaciones"
        className="relative flex-shrink-0 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-turquoise-500/50"
      >
        <Bell size={19} />
        {sinLeer > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-[17px] text-white text-center">
            {sinLeer > 99 ? '99+' : sinLeer}
          </span>
        )}
        <span className="sr-only">Notificaciones</span>
      </button>

      {abierta && (
        <div className="fixed left-3 right-3 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 z-50 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-700">Notificaciones</span>
            {sinLeer > 0 && (
              <button type="button" onClick={leerTodas} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-primary-600">
                <CheckCheck size={14} /> Marcar todas como leídas
              </button>
            )}
          </div>

          {lista.length === 0 ? (
            <div className="flex flex-col items-center gap-1 py-8 text-sm text-slate-400">
              <CheckCircle2 size={22} className="text-green-500" />
              Nada pendiente en esta empresa
            </div>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
              {lista.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => abrirAviso(n)}
                    className={`w-full flex items-start gap-2.5 px-4 py-2.5 text-left hover:bg-slate-50 ${n.leida ? '' : 'bg-turquoise-50/40'}`}
                  >
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${COLOR_NIVEL[n.nivel] || COLOR_NIVEL.media}`} />
                    <span className="flex-1 min-w-0">
                      <span className={`block text-sm ${n.leida ? 'text-slate-600' : 'font-semibold text-slate-800'}`}>{n.titulo}</span>
                      {n.mensaje && <span className="block text-xs text-slate-500">{n.mensaje}</span>}
                      <span className="block text-[11px] text-slate-400 mt-0.5">Desde {formatFechaHora(n.creado_en)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50 px-4 py-2.5 text-xs text-slate-500">
            <Mail size={14} className="shrink-0" />
            <span className="flex-1">Resumen por correo a las 7 am</span>
            <select
              value={preferencia?.frecuencia || ''}
              disabled={!preferencia}
              onChange={(e) => cambiarFrecuencia(e.target.value)}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
            >
              {!preferencia && <option value="">…</option>}
              {FRECUENCIAS.map((f) => <option key={f.valor} value={f.valor}>{f.label}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
